import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import TeacherIcon from "./../../assets/images/teacher.png";
import RedSchoolIcon from "./../../assets/images/redschool.png";
import GreenSchoolIcon from "./../../assets/images/greenschool.png";
import api from "services/api";
import Loading from "components/Loading";
import Select2Field from "components/Select2Field";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import { useFormik } from "formik";
import { createLineRenderer } from "utils/lineRenderer";
import { useTranslate } from "utils/i18n";
import { parseListPayload } from "utils/paginationStats";
import { teacherDetailHref } from "utils/teacherRoutes";
import { buildTeacherAvatarHtml, escapeHtml as escapeHtmlShared } from "utils/teacherAvatar";
import {
  getEstablishmentsMap,
  getWebDepartments,
  getWebProvinces,
  getWebSubPrefectures,
} from "services/establishmentService";
import {
  compactEstablishmentFilters,
  establishmentStatusOptions,
  establishmentTypeOptions,
} from "services/establishmentFilters";

const BRIGHT_PALETTE = [
  "#FF6B6B",
  "#4ECDC4",
  "#FFD166",
  "#A29BFE",
  "#FF8C42",
  "#6EE7B7",
  "#8E44AD",
  "#00B4D8",
  "#F72585",
  "#43AA8B",
];

function shuffle(arr) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("tk446655440000")}` };
}

function normalizeEstablishment(row) {
  const lat = parseFloat(
    row?.latitude ?? row?.Latitude ?? row?.lat ?? row?.estLatitude ?? row?.EstLatitude
  );
  const lng = parseFloat(
    row?.longitude ?? row?.Longitude ?? row?.lng ?? row?.estLongitude ?? row?.EstLongitude
  );
  const attendanceRaw = row?.attendanceCount ?? row?.AttendanceCount;
  const attendanceCount = Number(attendanceRaw);
  return {
    id: row?.id ?? row?.Id ?? row?.estabId ?? row?.EstabId,
    name: row?.name ?? row?.Name ?? row?.establishment ?? row?.estName ?? row?.EstName ?? "",
    type: row?.type ?? row?.Type ?? row?.estType ?? row?.EstType ?? "",
    status: row?.status ?? row?.Status ?? row?.establishmentStatus ?? row?.EstablishmentStatus ?? "",
    latitude: Number.isFinite(lat) ? lat : null,
    longitude: Number.isFinite(lng) ? lng : null,
    attendanceCount: Number.isFinite(attendanceCount) ? attendanceCount : 0,
  };
}

function computeMedian(values) {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

function getAttendanceTier(count, establishments) {
  const n = Number(count) || 0;
  const counts = establishments.map((e) => Number(e.attendanceCount) || 0);
  const unique = [...new Set(counts)];
  if (unique.length <= 1) return n > 0 ? "high" : "low";
  const threshold = computeMedian(counts);
  return n > threshold ? "high" : "low";
}

function bindEstablishmentNameTooltip(marker, name) {
  marker.bindTooltip(escapeHtml(name || "—"), {
    direction: "top",
    offset: [0, -48],
    opacity: 1,
    className: "map-estab-tooltip",
  });
}

function createEstablishmentIcon({ tier, selected = false }) {
  const width = selected ? 48 : 38;
  const height = selected ? 58 : 46;
  const iconUrl = tier === "high" ? GreenSchoolIcon : RedSchoolIcon;
  const tierClass = tier === "high" ? "map-estab-pin--high" : "map-estab-pin--low";
  const selectedClass = selected ? " map-estab-pin--selected" : "";
  return L.icon({
    iconUrl,
    iconSize: [width, height],
    iconAnchor: [width / 2, height],
    popupAnchor: [0, -height + 10],
    className: `map-estab-pin ${tierClass}${selectedClass}`,
  });
}

function getTeacherId(row) {
  return row?.id ?? row?.Id ?? row?.teacherId ?? row?.TeacherId;
}

function getTeacherName(row) {
  const first = row?.name ?? row?.Name ?? "";
  const last = row?.preName ?? row?.PreName ?? row?.lastName ?? "";
  return [first, last].filter(Boolean).join(" ") || "—";
}

function escapeHtml(value) {
  return escapeHtmlShared(value);
}

function viewProfileLink(label, row) {
  if (!row) return "";
  const href = teacherDetailHref(row);
  if (!href || href === "/enseignantes") return "";
  return `<div class="map-popup-actions"><a href="${href}" class="map-popup-btn">${escapeHtml(
    label
  )}</a></div>`;
}

const TEACHER_POPUP_PREVIEW_LIMIT = 4;

function renderTeacherLinks(teachers, { expanded, viewMoreLabel, showLessLabel }) {
  const total = teachers.length;
  const visible = expanded ? teachers : teachers.slice(0, TEACHER_POPUP_PREVIEW_LIMIT);
  const hiddenCount = total - TEACHER_POPUP_PREVIEW_LIMIT;

  const items = visible
    .map((row) => {
      const id = getTeacherId(row);
      const name = escapeHtml(getTeacherName(row));
      const status = escapeHtml(row?.status ?? row?.Status ?? "");
      const statusPart = status ? `<span class="map-popup-status">${status}</span>` : "";
      const avatar = buildTeacherAvatarHtml(row, 24);
      if (!id) {
        return `<div class="map-popup-teacher">${avatar}<span class="map-popup-teacher-name">${name}${statusPart}</span></div>`;
      }
      const href = teacherDetailHref(row);
      return `<div class="map-popup-teacher">${avatar}<span class="map-popup-teacher-name"><a href="${href}">${name}</a>${statusPart}</span></div>`;
    })
    .join("");

  let toggle = "";
  if (!expanded && hiddenCount > 0) {
    toggle = `<button type="button" class="map-popup-view-more" data-map-view-more="1">${escapeHtml(
      viewMoreLabel.replace("{{count}}", String(hiddenCount))
    )}</button>`;
  } else if (expanded && total > TEACHER_POPUP_PREVIEW_LIMIT) {
    toggle = `<button type="button" class="map-popup-show-less" data-map-show-less="1">${escapeHtml(
      showLessLabel
    )}</button>`;
  }

  return `${items}${toggle}`;
}

function bindEstablishmentPopupHandlers(container, { onExpand, onCollapse }) {
  if (!container) return;
  const viewMore = container.querySelector("[data-map-view-more]");
  const showLess = container.querySelector("[data-map-show-less]");
  if (viewMore) {
    viewMore.onclick = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      onExpand?.();
    };
  }
  if (showLess) {
    showLess.onclick = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      onCollapse?.();
    };
  }
}

function buildEstablishmentPopup(estab, teachers, labels, expanded = false) {
  const {
    loadingTeachers,
    noTeachersLabel,
    teachersLabel,
    clickHintLabel,
    viewProfileLabel,
    viewMoreLabel,
    showLessLabel,
    attendanceLabel,
  } = labels;
  const header = `<div class="map-popup-title">${escapeHtml(estab.name)}</div>`;
  const typeLine = estab.type ? `<div class="map-popup-meta">Type: ${escapeHtml(estab.type)}</div>` : "";
  const attendanceLine = `<div class="map-popup-meta">${escapeHtml(attendanceLabel || "Attendance")}: ${Number(estab.attendanceCount ?? 0)}</div>`;

  if (loadingTeachers) {
    return `${header}${typeLine}${attendanceLine}<div class="map-popup-loading">${escapeHtml(teachersLabel)}…</div>`;
  }

  if (!teachers.length) {
    const hint = clickHintLabel || noTeachersLabel;
    return `${header}${typeLine}${attendanceLine}<div class="map-popup-empty">${escapeHtml(hint)}</div>`;
  }

  const links = renderTeacherLinks(teachers, {
    expanded,
    viewMoreLabel: viewMoreLabel || "View {{count}} more",
    showLessLabel: showLessLabel || "Show less",
  });

  const singleTeacherLink = teachers.length === 1 ? viewProfileLink(viewProfileLabel, teachers[0]) : "";

  return `${header}${typeLine}${attendanceLine}<div class="map-popup-section">${escapeHtml(teachersLabel)} (${teachers.length})</div>${links}${singleTeacherLink}`;
}

function buildTeacherPopup(row, viewProfileLabel) {
  const name = escapeHtml(getTeacherName(row));
  const status = escapeHtml(row?.status ?? row?.Status ?? "");
  const imei = escapeHtml(row?.imei ?? row?.Imei ?? "—");
  const avatar = buildTeacherAvatarHtml(row, 36);
  return `<div class="map-popup-header">${avatar}<div class="map-popup-header-text"><div class="map-popup-title">${name}</div></div></div><div class="map-popup-meta">Status: ${status}</div><div class="map-popup-meta">IMEI: ${imei}</div>${viewProfileLink(
    viewProfileLabel,
    row
  )}`;
}


function boundsFromSelection(estab, teachers) {
  const bounds = L.latLngBounds([]);
  const extend = (lat, lng) => {
    if (Number.isFinite(lat) && Number.isFinite(lng)) bounds.extend([lat, lng]);
  };

  if (estab?.latitude != null && estab?.longitude != null) {
    extend(estab.latitude, estab.longitude);
  }

  (teachers || []).forEach((row) => {
    extend(parseFloat(row?.attLatitude ?? row?.AttLatitude), parseFloat(row?.attLongitude ?? row?.AttLongitude));
    extend(parseFloat(row?.estLatitude ?? row?.EstLatitude), parseFloat(row?.estLongitude ?? row?.EstLongitude));
  });

  if (!bounds.isValid()) return null;

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const latSpan = Math.abs(ne.lat - sw.lat);
  const lngSpan = Math.abs(ne.lng - sw.lng);
  if (latSpan < 0.002 || lngSpan < 0.002) {
    const center = bounds.getCenter();
    const pad = 0.004;
    bounds.extend([center.lat + pad, center.lng + pad]);
    bounds.extend([center.lat - pad, center.lng - pad]);
  }

  return bounds;
}

function getMapFitPadding() {
  const w = typeof window !== "undefined" ? window.innerWidth : 1024;
  const toolbarTop = w < 640 ? 118 : 96;
  const side = w < 640 ? 52 : 60;
  const bottom = w < 640 ? 80 : 52;
  return {
    paddingTopLeft: L.point(side, toolbarTop),
    paddingBottomRight: L.point(side, bottom),
  };
}

function fitMapToAllEstablishments(map, establishments) {
  const bounds = [];
  (establishments || []).forEach((estab) => {
    if (estab.latitude != null && estab.longitude != null) {
      bounds.push([estab.latitude, estab.longitude]);
    }
  });
  if (!bounds.length || !map) return;

  const fitBounds = L.latLngBounds(bounds);
  const { paddingTopLeft, paddingBottomRight } = getMapFitPadding();
  map.flyToBounds(fitBounds, {
    paddingTopLeft,
    paddingBottomRight,
    maxZoom: 15,
    duration: 1.1,
    easeLinearity: 0.25,
  });
}

function fitMapToSelection(map, estab, teachers) {
  const bounds = boundsFromSelection(estab, teachers);
  if (!bounds || !map) return Promise.resolve();

  const { paddingTopLeft, paddingBottomRight } = getMapFitPadding();

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      map.off("moveend", finish);
      resolve();
    };
    map.once("moveend", finish);
    map.flyToBounds(bounds, {
      paddingTopLeft,
      paddingBottomRight,
      maxZoom: 17,
      duration: 0.95,
      easeLinearity: 0.22,
    });
    window.setTimeout(finish, 1100);
  });
}

function fadeInMarker(marker, delayMs = 0) {
  window.setTimeout(() => {
    marker.setOpacity(0);
    let opacity = 0;
    const step = () => {
      opacity = Math.min(1, opacity + 0.12);
      marker.setOpacity(opacity);
      if (opacity < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, delayMs);
}

export default function NewMapPage() {
  const [establishments, setEstablishments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedEstabId, setSelectedEstabId] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [teachersRevealReady, setTeachersRevealReady] = useState(false);
  const [popupTeachersExpanded, setPopupTeachersExpanded] = useState(false);

  const [provOpts, setProvOpts] = useState([]);
  const [deptOpts, setDeptOpts] = useState([]);
  const [subpOpts, setSubpOpts] = useState([]);

  const { t } = useTranslate();

  const mapRef = useRef(null);
  const estabLayerRef = useRef(null);
  const teacherLayerRef = useRef(null);
  const linesLayerRef = useRef(null);
  const drawLineRef = useRef(null);
  const markerByEstabIdRef = useRef(new Map());
  const colorQueueRef = useRef(shuffle(BRIGHT_PALETTE));
  const selectEstablishmentRef = useRef(async () => {});
  const setSelectSchoolRef = useRef(() => {});
  const establishmentsRef = useRef([]);
  const popupLabelsRef = useRef({});
  const selectionTokenRef = useRef(0);
  const cancelAnimationsRef = useRef([]);
  const selectedEstabIdRef = useRef("");
  const geoDefaultsDoneRef = useRef(false);
  const establishmentRequestRef = useRef(0);
  const establishmentFiltersRef = useRef({ type: "", status: "" });
  const formikSetRef = useRef(() => {});
  const popupToggleHandlersRef = useRef({ onExpand: () => {}, onCollapse: () => {} });

  popupToggleHandlersRef.current = {
    onExpand: () => setPopupTeachersExpanded(true),
    onCollapse: () => setPopupTeachersExpanded(false),
  };

  const getNextColor = () => {
    if (colorQueueRef.current.length === 0) {
      colorQueueRef.current = shuffle(BRIGHT_PALETTE);
    }
    return colorQueueRef.current.shift();
  };

  const popupLabels = {
    noTeachersLabel: t("map.no_teachers", "No teachers found for this date."),
    teachersLabel: t("map.teachers_at_establishment", "Teachers"),
    clickHintLabel: t("map.click_for_teachers", "Click to load teachers."),
    viewProfileLabel: t("map.view_profile", "View Profile"),
    viewMoreLabel: t("map.view_more_teachers", "View {{count}} more"),
    showLessLabel: t("map.show_less_teachers", "Show less"),
    attendanceLabel: t("map.attendance_count", "Attendance count"),
  };
  popupLabelsRef.current = popupLabels;

  const clearTeacherOverlay = useCallback(() => {
    cancelAnimationsRef.current.forEach((cancel) => cancel?.());
    cancelAnimationsRef.current = [];
    teacherLayerRef.current?.clearLayers();
    linesLayerRef.current?.clearLayers();
    mapRef.current?.closePopup();
  }, []);

  const clearLinesAndTeachers = useCallback(() => {
    cancelAnimationsRef.current.forEach((cancel) => cancel?.());
    cancelAnimationsRef.current = [];
    teacherLayerRef.current?.clearLayers();
    linesLayerRef.current?.clearLayers();
  }, []);

  const resetMarkerHighlights = useCallback(() => {
    const list = establishmentsRef.current;
    markerByEstabIdRef.current.forEach((marker, id) => {
      const estab = list.find((e) => String(e.id) === String(id));
      const tier = getAttendanceTier(estab?.attendanceCount, list);
      marker.setIcon(createEstablishmentIcon({ tier, selected: false }));
      marker.setZIndexOffset(0);
    });
  }, []);

  const setSelectedMarkerHighlight = useCallback((estabId) => {
    const list = establishmentsRef.current;
    markerByEstabIdRef.current.forEach((marker, id) => {
      const estab = list.find((e) => String(e.id) === String(id));
      const tier = getAttendanceTier(estab?.attendanceCount, list);
      const isSelected = String(id) === String(estabId);
      marker.setIcon(createEstablishmentIcon({ tier, selected: isSelected }));
      marker.setZIndexOffset(isSelected ? 1000 : 0);
    });
  }, []);

  const syncEstablishmentMarkerVisibility = useCallback((selectedId) => {
    const layer = estabLayerRef.current;
    if (!layer) return;

    markerByEstabIdRef.current.forEach((marker, id) => {
      const shouldShow = !selectedId || String(id) === String(selectedId);
      if (shouldShow) {
        if (!layer.hasLayer(marker)) marker.addTo(layer);
        marker.setOpacity(1);
      } else if (layer.hasLayer(marker)) {
        layer.removeLayer(marker);
      }
    });
  }, []);

  const fetchDepartments = useCallback(async (provid) => {
    if (!provid) {
      setDeptOpts([]);
      return;
    }
    try {
      setDeptOpts(await getWebDepartments(provid));
    } catch (err) {
      console.error("Fetch departments error", err);
      setDeptOpts([]);
    }
  }, []);

  const fetchSubprefectures = useCallback(async (dptid) => {
    if (!dptid) {
      setSubpOpts([]);
      return;
    }
    try {
      setSubpOpts(await getWebSubPrefectures(dptid));
    } catch (err) {
      console.error("Fetch subprefectures error", err);
      setSubpOpts([]);
    }
  }, []);

  const fetchEstablishments = useCallback(async (filters = {}) => {
    const requestId = ++establishmentRequestRef.current;
    setLoading(true);
    try {
      const data = await getEstablishmentsMap(filters);
      if (requestId !== establishmentRequestRef.current) return;
      const list = data.map(normalizeEstablishment).filter((e) => e.id);
      setEstablishments(list);
    } catch (err) {
      console.error("API Error:", err);
      if (requestId === establishmentRequestRef.current) setEstablishments([]);
    } finally {
      if (requestId === establishmentRequestRef.current) setLoading(false);
    }
  }, []);

  const fetchTeachersForEstablishment = useCallback(async (estabId, dateValue = "") => {
    if (!estabId) return [];
    try {
      const params = { EstabId: estabId, pageNumber: 1, pageSize: 500 };
      Object.assign(params, compactEstablishmentFilters(establishmentFiltersRef.current));
      if (dateValue) {
        params.AttendDate = format(new Date(dateValue), "yyyy-MM-dd");
      }
      const res = await api.get("/Teacher/scoresummary", {
        params,
        headers: authHeaders(),
      });
      return parseListPayload(res.data);
    } catch (err) {
      console.error("API Error:", err);
      return [];
    }
  }, []);

  establishmentsRef.current = establishments;
  selectedEstabIdRef.current = selectedEstabId;

  const updateEstablishmentPopup = useCallback((estabId, teacherRows, isLoading, expanded = false) => {
    const marker = markerByEstabIdRef.current.get(String(estabId));
    const estab = establishmentsRef.current.find((e) => String(e.id) === String(estabId));
    if (!marker || !estab) return;

    const popup = marker.getPopup();
    if (popup) {
      popup.options.className = expanded
        ? "map-custom-popup map-custom-popup--expanded"
        : "map-custom-popup";
    }

    marker.setPopupContent(
      buildEstablishmentPopup(estab, teacherRows, {
        ...popupLabelsRef.current,
        loadingTeachers: isLoading,
      }, expanded)
    );

    const popupEl = popup?.getElement?.();
    if (popupEl) {
      popupEl.classList.toggle("map-custom-popup--expanded", expanded);
    }
    bindEstablishmentPopupHandlers(popupEl, popupToggleHandlersRef.current);
  }, []);

  const selectEstablishment = useCallback(
    async (estabId, { openPopup = true } = {}) => {
      const token = ++selectionTokenRef.current;

      clearTeacherOverlay();
      setTeachers([]);
      setTeachersRevealReady(false);
      setPopupTeachersExpanded(false);

      if (!estabId) {
        setSelectedEstabId("");
        resetMarkerHighlights();
        syncEstablishmentMarkerVisibility("");
        return;
      }

      const idStr = String(estabId);
      setSelectedEstabId(idStr);
      setSelectedMarkerHighlight(idStr);
      syncEstablishmentMarkerVisibility(idStr);
      updateEstablishmentPopup(idStr, [], true);

      const marker = markerByEstabIdRef.current.get(idStr);
      const map = mapRef.current;
      const estab = establishmentsRef.current.find((e) => String(e.id) === idStr);

      if (token !== selectionTokenRef.current) return;

      if (openPopup && marker) marker.openPopup();

      setLoadingTeachers(true);
      const list = await fetchTeachersForEstablishment(idStr, "");
      setLoadingTeachers(false);

      if (token !== selectionTokenRef.current) return;

      if (map && estab) {
        await fitMapToSelection(map, estab, list);
      }

      if (token !== selectionTokenRef.current) return;

      setTeachers(list);
      setTeachersRevealReady(true);
      updateEstablishmentPopup(idStr, list, false);
      if (openPopup && marker) marker.openPopup();
    },
    [fetchTeachersForEstablishment, updateEstablishmentPopup, clearTeacherOverlay, setSelectedMarkerHighlight, resetMarkerHighlights, syncEstablishmentMarkerVisibility]
  );

  const formik = useFormik({
    initialValues: {
      province: "",
      department: "",
      subprefecture: "",
      selectschool: "",
      type: "",
      status: "",
    },
    onSubmit: async (values) => {
      if (values.selectschool) {
        await selectEstablishment(values.selectschool, { openPopup: true });
      } else {
        clearMapSelection();
        await fetchEstablishments({
          provinceId: values.province,
          departmentId: values.department,
          subPrefectureId: values.subprefecture,
          type: values.type,
          status: values.status,
        });
      }
    },
  });

  establishmentFiltersRef.current = {
    type: formik.values.type,
    status: formik.values.status,
  };

  const typeOptions = useMemo(() => establishmentTypeOptions(t), [t]);
  const statusOptions = useMemo(() => establishmentStatusOptions(t), [t]);

  selectEstablishmentRef.current = selectEstablishment;
  setSelectSchoolRef.current = (val) => formikSetRef.current("selectschool", val);

  const clearMapSelection = useCallback(() => {
    selectionTokenRef.current += 1;
    clearTeacherOverlay();
    setSelectedEstabId("");
    setTeachers([]);
    setTeachersRevealReady(false);
    setPopupTeachersExpanded(false);
    resetMarkerHighlights();
    syncEstablishmentMarkerVisibility("");
    formikSetRef.current("selectschool", "");

    const map = mapRef.current;
    if (map && establishmentsRef.current.length) {
      fitMapToAllEstablishments(map, establishmentsRef.current);
    }
  }, [clearTeacherOverlay, resetMarkerHighlights, syncEstablishmentMarkerVisibility]);

  formikSetRef.current = formik.setFieldValue;

  useEffect(() => {
    const initGeography = async () => {
      if (geoDefaultsDoneRef.current) return;
      try {
        geoDefaultsDoneRef.current = true;
        const provinces = await getWebProvinces();
        setProvOpts(provinces);
        await fetchEstablishments();
      } catch (err) {
        console.error("Geography init error", err);
      }
    };
    initGeography();
  }, [fetchEstablishments]);

  useEffect(() => {
    if (mapRef.current) return;

    const map = L.map("map", {
      preferCanvas: true,
      zoomAnimation: true,
      fadeAnimation: true,
      markerZoomAnimation: true,
    });
    mapRef.current = map;
    const { drawLine } = createLineRenderer(map);
    drawLineRef.current = drawLine;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
      maxZoom: 19,
    }).addTo(map);

    estabLayerRef.current = L.layerGroup().addTo(map);
    teacherLayerRef.current = L.layerGroup().addTo(map);
    linesLayerRef.current = L.layerGroup().addTo(map);

    map.setView([0.0, 20.0], 5);

    const onPopupOpen = (e) => {
      bindEstablishmentPopupHandlers(e.popup?.getElement?.(), popupToggleHandlersRef.current);
    };
    map.on("popupopen", onPopupOpen);

    return () => {
      map.off("popupopen", onPopupOpen);
      clearTeacherOverlay();
      map.remove();
      mapRef.current = null;
      estabLayerRef.current = null;
      teacherLayerRef.current = null;
      linesLayerRef.current = null;
      markerByEstabIdRef.current.clear();
    };
  }, [clearTeacherOverlay]);

  useEffect(() => {
    const estabLayer = estabLayerRef.current;
    const map = mapRef.current;
    if (!estabLayer || !map) return;

    estabLayer.clearLayers();
    markerByEstabIdRef.current.clear();

    const bounds = [];
    const labels = popupLabelsRef.current;

    establishments.forEach((estab, index) => {
      if (estab.latitude == null || estab.longitude == null) return;

      const tier = getAttendanceTier(estab.attendanceCount, establishments);
      const marker = L.marker([estab.latitude, estab.longitude], {
        icon: createEstablishmentIcon({ tier, selected: false }),
        opacity: 0,
      }).addTo(estabLayer);

      fadeInMarker(marker, index * 35);
      markerByEstabIdRef.current.set(String(estab.id), marker);
      bounds.push([estab.latitude, estab.longitude]);

      marker.bindPopup(
        buildEstablishmentPopup(estab, [], {
          ...labels,
          loadingTeachers: false,
          noTeachersLabel: t("map.no_teachers", "No teachers found for this date."),
        }),
        { className: "map-custom-popup", maxWidth: 280 }
      );
      bindEstablishmentNameTooltip(marker, estab.name);

      marker.on("click", () => {
        setSelectSchoolRef.current(String(estab.id));
        selectEstablishmentRef.current(estab.id, { openPopup: true });
      });
    });

    if (selectedEstabIdRef.current) {
      syncEstablishmentMarkerVisibility(selectedEstabIdRef.current);
      setSelectedMarkerHighlight(selectedEstabIdRef.current);
    }

    if (bounds.length > 0 && !selectedEstabIdRef.current) {
      const fitBounds = L.latLngBounds(bounds);
      const { paddingTopLeft, paddingBottomRight } = getMapFitPadding();
      map.flyToBounds(fitBounds, {
        paddingTopLeft,
        paddingBottomRight,
        maxZoom: 15,
        duration: 1.1,
        easeLinearity: 0.25,
      });
    }
  }, [establishments, t, syncEstablishmentMarkerVisibility, setSelectedMarkerHighlight]);

  useEffect(() => {
    const teacherLayer = teacherLayerRef.current;
    const linesLayer = linesLayerRef.current;
    const drawLine = drawLineRef.current;
    if (!teacherLayer || !linesLayer || !drawLine) return;

    clearLinesAndTeachers();

    if (!selectedEstabId || !teachers.length || !teachersRevealReady) return;

    const selectedEstab = establishmentsRef.current.find((e) => String(e.id) === String(selectedEstabId));
    const estabPoint =
      selectedEstab?.latitude != null && selectedEstab?.longitude != null
        ? [selectedEstab.latitude, selectedEstab.longitude]
        : null;

    const personIcon = L.icon({
      iconUrl: TeacherIcon,
      iconSize: [36, 50],
      iconAnchor: [18, 50],
      popupAnchor: [0, -44],
      className: "map-teacher-icon",
    });

    const viewProfileLabel = popupLabelsRef.current.viewProfileLabel;
    let lineIndex = 0;

    teachers.forEach((row) => {
      const attLat = parseFloat(row?.attLatitude ?? row?.AttLatitude);
      const attLng = parseFloat(row?.attLongitude ?? row?.AttLongitude);
      const estLat = parseFloat(row?.estLatitude ?? row?.EstLatitude);
      const estLng = parseFloat(row?.estLongitude ?? row?.EstLongitude);

      const lineFrom =
        Number.isFinite(estLat) && Number.isFinite(estLng)
          ? [estLat, estLng]
          : estabPoint;
      const lineTo = Number.isFinite(attLat) && Number.isFinite(attLng) ? [attLat, attLng] : null;

      if (lineFrom && lineTo) {
        const color = getNextColor();
        const stagger = lineIndex * 120;
        let teacherMarker = null;

        const { cancelAnimation } = drawLineRef.current(lineFrom, lineTo, color, {
          layerGroup: linesLayer,
          animate: true,
          duration: 820,
          delay: stagger,
          onComplete: () => {
            if (!teacherMarker) return;
            fadeInMarker(teacherMarker, 0);
          },
        });
        if (cancelAnimation) cancelAnimationsRef.current.push(cancelAnimation);

        if (Number.isFinite(attLat) && Number.isFinite(attLng)) {
          teacherMarker = L.marker([attLat, attLng], { icon: personIcon, opacity: 0 }).addTo(teacherLayer);
          teacherMarker.bindPopup(buildTeacherPopup(row, viewProfileLabel), {
            className: "map-custom-popup",
            maxWidth: 260,
          });
        }

        lineIndex += 1;
      } else if (Number.isFinite(attLat) && Number.isFinite(attLng)) {
        const marker = L.marker([attLat, attLng], { icon: personIcon, opacity: 0 }).addTo(teacherLayer);
        marker.bindPopup(buildTeacherPopup(row, viewProfileLabel), {
          className: "map-custom-popup",
          maxWidth: 260,
        });
        fadeInMarker(marker, lineIndex * 80);
        lineIndex += 1;
      }
    });
  }, [teachers, selectedEstabId, teachersRevealReady, clearLinesAndTeachers]);

  useEffect(() => {
    if (!selectedEstabId) return;
    updateEstablishmentPopup(selectedEstabId, teachers, loadingTeachers, popupTeachersExpanded);
    const marker = markerByEstabIdRef.current.get(String(selectedEstabId));
    if (marker?.isPopupOpen?.()) {
      marker.openPopup();
    }
  }, [selectedEstabId, teachers, loadingTeachers, popupTeachersExpanded, updateEstablishmentPopup]);

  const mappableCount = establishments.filter((e) => e.latitude != null && e.longitude != null).length;

  if (loading && establishments.length === 0 && provOpts.length === 0) {
    return (
      <p>
        <Loading />
      </p>
    );
  }

  const filterField = (label, name, options, value, onChange, disabled = false, wide = false) => (
    <div className={`map-toolbar-field${wide ? " map-toolbar-field--wide" : ""}`}>
      <label className="map-toolbar-label">{label}</label>
      <Select2Field
        name={name}
        options={options}
        placeholder={label}
        value={value}
        disabled={disabled}
        onChange={onChange}
        allowClear={false}
        dropdownParent={typeof document !== "undefined" ? document.body : undefined}
      />
    </div>
  );

  return (
    <div className="map-page-root">
      <SoftBox component="form" role="form" onSubmit={formik.handleSubmit}>
        <div className="map-toolbar">
          <div className="map-toolbar-grid">
          {filterField(
            t("assignments.province", "Province"),
            "province",
            [{ id: "", text: t("assignments.province_placeholder", "Province") }, ...provOpts],
            formik.values.province,
            (val) => {
              formik.setFieldValue("province", val);
              formik.setFieldValue("department", "");
              formik.setFieldValue("subprefecture", "");
              formik.setFieldValue("selectschool", "");
              setDeptOpts([]);
              setSubpOpts([]);
              setEstablishments([]);
              clearMapSelection();
              if (val) fetchDepartments(val);
              fetchEstablishments({
                provinceId: val,
                type: formik.values.type,
                status: formik.values.status,
              });
            }
          )}

          {filterField(
            t("assignments.department", "Department"),
            "department",
            [{ id: "", text: t("assignments.department_placeholder", "Department") }, ...deptOpts],
            formik.values.department,
            (val) => {
              formik.setFieldValue("department", val);
              formik.setFieldValue("subprefecture", "");
              formik.setFieldValue("selectschool", "");
              setSubpOpts([]);
              setEstablishments([]);
              clearMapSelection();
              if (val) fetchSubprefectures(val);
              fetchEstablishments({
                provinceId: formik.values.province,
                departmentId: val,
                type: formik.values.type,
                status: formik.values.status,
              });
            },
            !formik.values.province
          )}

          {filterField(
            t("assignments.subprefecture", "Sub-prefecture"),
            "subprefecture",
            [{ id: "", text: t("assignments.subprefecture_placeholder", "Sub-prefecture") }, ...subpOpts],
            formik.values.subprefecture,
            (val) => {
              formik.setFieldValue("subprefecture", val);
              formik.setFieldValue("selectschool", "");
              setEstablishments([]);
              clearMapSelection();
              fetchEstablishments({
                provinceId: formik.values.province,
                departmentId: formik.values.department,
                subPrefectureId: val,
                type: formik.values.type,
                status: formik.values.status,
              });
            },
            !formik.values.department
          )}

          {filterField(
            t("establishments.col.type", "Type"),
            "type",
            typeOptions,
            formik.values.type,
            (val) => {
              formik.setFieldValue("type", val);
              formik.setFieldValue("selectschool", "");
              clearMapSelection();
              fetchEstablishments({
                provinceId: formik.values.province,
                departmentId: formik.values.department,
                subPrefectureId: formik.values.subprefecture,
                type: val,
                status: formik.values.status,
              });
            }
          )}

          {filterField(
            t("establishments.col.status", "Status"),
            "status",
            statusOptions,
            formik.values.status,
            (val) => {
              formik.setFieldValue("status", val);
              formik.setFieldValue("selectschool", "");
              clearMapSelection();
              fetchEstablishments({
                provinceId: formik.values.province,
                departmentId: formik.values.department,
                subPrefectureId: formik.values.subprefecture,
                type: formik.values.type,
                status: val,
              });
            }
          )}

          {filterField(
            t("map.select_establishment", "Establishment"),
            "selectschool",
            [
              { id: "", text: t("map.select_establishment_placeholder", "Establishment") },
              ...establishments.map((school) => ({ id: school.id, text: school.name })),
            ],
            formik.values.selectschool,
            (val) => {
              formik.setFieldValue("selectschool", val);
              if (val) selectEstablishment(val, { openPopup: true });
              else clearMapSelection();
            },
            loading || establishments.length === 0,
            true
          )}

          <div className="map-toolbar-field map-toolbar-field--action">
            <SoftButton
              type="submit"
              className="map-toolbar-search"
              variant="gradient"
              color="info"
              disabled={loading || loadingTeachers}
            >
              {loadingTeachers ? t("map.loading_teachers", "Loading…") : t("home.search", "Search")}
            </SoftButton>
          </div>
          </div>
        </div>
      </SoftBox>

      <div className="map-status-card">
          <>
            <span className="map-status-chip">
              {t("map.establishments_on_map", "{{count}} on map").replace("{{count}}", String(mappableCount))}
            </span>
            {mappableCount > 0 && (
              <>
                <span className="map-status-chip map-status-legend">
                  <img src={GreenSchoolIcon} alt="" className="map-legend-pin" aria-hidden="true" />
                  {t("map.attendance_high", "Higher attendance")}
                </span>
                <span className="map-status-chip map-status-legend">
                  <img src={RedSchoolIcon} alt="" className="map-legend-pin" aria-hidden="true" />
                  {t("map.attendance_low", "Lower attendance")}
                </span>
              </>
            )}
            {selectedEstabId && (
              <span className="map-status-chip map-status-chip--accent">
                {loadingTeachers
                  ? t("map.loading_teachers", "Loading teachers…")
                  : t("map.teachers_shown", "{{count}} check-in(s)").replace("{{count}}", String(teachers.length))}
              </span>
            )}
            {selectedEstabId && (
              <span className="map-status-chip">
                {t("map.focused_establishment", "1 establishment shown")}
              </span>
            )}
            {selectedEstabId && (
              <button
                type="button"
                className="map-status-chip map-status-chip--action"
                onClick={clearMapSelection}
              >
                {t("map.clear_selection", "Show all establishments")}
              </button>
            )}
          </>
      </div>

      {loading && (
        <div className="map-loading-overlay">
          <Loading />
        </div>
      )}

      <div id="map" className="map-canvas" />
    </div>
  );
}
